import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

type Tab = 'rdn' | 'br_oper' | 'br_fact';
type View = 'chart' | 'table';

interface HourRow { hour: number; price: number | null; volume: number | null; hh: string }
interface DamResp {
  tradeDate: string | null;
  hours: { hour: number; price: number | null; volume: number | null }[];
}

interface BrHour { hour: number; pos: number | null; neg: number | null; imb: number | null; hh: string }
interface BrSummary {
  minPos: number | null; maxPos: number | null;
  minNeg: number | null; maxNeg: number | null;
}
interface BrResp {
  tradeDate: string | null;
  isLatestDefault: boolean;
  dataRange: { min: string | null; max: string | null };
  hours: { hour: number; pos: number | null; neg: number | null; imb: number | null }[];
  summary: BrSummary | null;
}

interface Zone { wavg: number | null; prevWavg: number | null; changePct: number | null }
interface DamSummary {
  tradeDate: string | null;
  dataRange: { min: string | null; max: string | null };
  tiles: {
    day: Zone; day_8_23: Zone; night: Zone; peak: Zone; semipeak: Zone;
    month: { ym: string; wavg: number | null; prevYm: string; prevWavg: number | null; changePct: number | null };
  } | null;
}

const PRICE = '#E7476B';   // pink price line (matches OREE)
const VOL = '#2E9BD6';     // blue volume bars
const POS_C = '#22C55E';   // positive imbalance — green
const NEG_C = '#EF4444';   // negative imbalance — red
const UP_C = '#22C55E';    // price growth — green (per OREE text-success)
const DOWN_C = '#EF4444';  // price fall — red (per OREE text-danger)
const PRICE_TICKS = [0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000];
const OREE_URL = 'https://www.oree.com.ua';
const UA_ENERGY_URL = 'https://ua.energy/uchasnikam_rinku/rezultaty-balansuyuchogo-rynku-2/';

const num = (n: number | null | undefined, d = 1) =>
  n == null ? '—' : Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });
const num2 = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');
const shiftDay = (iso: string, delta: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

/** Theme tokens shared by every block on this page. */
function useTokens() {
  const { isDark } = useTheme();
  return {
    isDark,
    cardBg: isDark ? '#1A1A1A' : '#FFFFFF',
    cardBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    textMain: isDark ? '#E0E0E0' : '#1A1A1A',
    textMuted: '#6B6B70',
    textDim: isDark ? '#4A4A50' : '#9B9BA4',
    innerBg: isDark ? '#0F0F0F' : '#F5F5F7',
    rowHi: isDark ? 'rgba(255,255,255,0.05)' : '#F0F1F3',
  };
}

/** ◀ дата ▶ picker. `bold` renders the date prominently (РДН header). */
function DateNav({ value, onChange, min, max, bold = false }:
  { value: string; onChange: (v: string) => void; min?: string | null; max?: string | null; bold?: boolean }) {
  const T = useTokens();
  const canPrev = !!value && (!min || value > min);
  const canNext = !!value && (!max || value < max);
  const btn = (dir: -1 | 1, enabled: boolean) => (
    <button onClick={() => enabled && onChange(shiftDay(value, dir))} disabled={!enabled}
      className="flex items-center justify-center rounded-full transition-colors"
      style={{
        width: bold ? 36 : 32, height: bold ? 36 : 32, background: T.innerBg,
        border: `1px solid ${T.cardBorder}`, color: enabled ? T.textMain : T.textDim,
        cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
      }}>
      <i className={dir === -1 ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'} style={{ fontSize: bold ? 20 : 18 }} />
    </button>
  );
  return (
    <div className="flex items-center gap-2">
      {btn(-1, canPrev)}
      <input type="date" value={value} min={min || undefined} max={max || undefined}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className="rounded-full outline-none cursor-pointer"
        style={{
          background: T.innerBg, border: `1px solid ${T.cardBorder}`, color: T.textMain,
          colorScheme: T.isDark ? 'dark' : 'light',
          padding: bold ? '9px 14px' : '8px 12px',
          fontSize: bold ? 15 : 14, fontWeight: bold ? 700 : 400,
        }} />
      {btn(1, canNext)}
    </div>
  );
}

/** Horizontal (transposed) hourly table: first row = hours, then one row per series. */
function TransposedTable({ hours, rows }:
  { hours: string[]; rows: { label: string; cells: string[]; color?: string }[] }) {
  const T = useTokens();
  const cell: any = {
    padding: '8px 10px', fontSize: 13, textAlign: 'center', color: T.textMain,
    borderBottom: `1px solid ${T.cardBorder}`, borderRight: `1px solid ${T.cardBorder}`, whiteSpace: 'nowrap',
  };
  const head: any = { ...cell, fontWeight: 700, color: T.textMuted, background: T.rowHi };
  const label: any = {
    ...cell, textAlign: 'left', fontWeight: 600, color: T.textMuted, background: T.cardBg,
    position: 'sticky', left: 0, minWidth: 190,
  };
  return (
    <div className="overflow-x-auto" style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 8 }}>
      <table style={{ borderCollapse: 'collapse', minWidth: 900 }}>
        <tbody>
          <tr>
            <td style={label}>Година</td>
            {hours.map((h) => <td key={h} style={head}>{h}</td>)}
          </tr>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={label}>{r.label}</td>
              {r.cells.map((c, i) => (
                <td key={i} style={{ ...cell, color: r.color || T.textMain, fontWeight: r.color ? 600 : 400 }}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Pill toggle used for Графік / Таблиця. */
function ViewToggle({ view, setView }: { view: View; setView: (v: View) => void }) {
  const T = useTokens();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {(([['chart', 'Графік'], ['table', 'Таблиця']]) as [View, string][]).map(([v, lbl]) => (
        <button key={v} onClick={() => setView(v)} className="text-sm rounded-full transition-colors"
          style={{
            padding: '8px 20px',
            background: view === v ? (T.isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6') : 'transparent',
            color: view === v ? T.textMain : T.textMuted,
            border: `1px solid ${view === v ? T.cardBorder : 'transparent'}`,
            fontWeight: view === v ? 600 : 400,
          }}>
          {lbl}
        </button>
      ))}
    </div>
  );
}

// ---- Balancing-market hourly prices (shared by «БР — фактичні» / «БР — оперативні») ----
function BrPricesView({ endpoint, title }: { endpoint: string; title: string }) {
  const T = useTokens();
  const [date, setDate] = useState('');            // '' → backend resolves to latest available
  const [data, setData] = useState<BrResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('chart');

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_BASE_URL}${endpoint}${date ? `?date=${date}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: BrResp) => { setData(d); if (!date && d.tradeDate) setDate(d.tradeDate); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [endpoint, date]);

  const hours: BrHour[] = useMemo(
    () => (data?.hours || []).map((h) => ({ ...h, hh: String(h.hour).padStart(2, '0') })), [data]);
  const min = data?.dataRange?.min || null;
  const max = data?.dataRange?.max || null;
  const sm = data?.summary;

  const chip = (label: string, value: number | null | undefined, color: string) => (
    <span key={label} className="inline-flex items-center gap-1 rounded-full"
      style={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, padding: '4px 10px', fontSize: 11, whiteSpace: 'nowrap' }}>
      <span style={{ color: T.textMuted }}>{label}</span>
      <b style={{ color }}>{num2(value)}</b>
    </span>
  );

  const BrTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const get = (k: string) => payload.find((p: any) => p.dataKey === k)?.value;
    return (
      <div style={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: T.textMain }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{dmy(data?.tradeDate || date)} · Година {label}:00</div>
        <div style={{ color: POS_C }}>ПНБ: {num2(get('pos'))} грн/МВт·год</div>
        <div style={{ color: NEG_C }}>ННБ: {num2(get('neg'))} грн/МВт·год</div>
      </div>
    );
  };

  return (
    <div className="rounded-xl p-4 sm:p-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="shrink-0">
          <div className="text-lg font-bold" style={{ color: T.textMain }}>{title}</div>
          <a href={UA_ENERGY_URL} target="_blank" rel="noopener noreferrer"
            className="text-xs mt-0.5 inline-block hover:underline" style={{ color: VOL }}>
            джерело: НЕК «Укренерго»
          </a>
        </div>
        {sm && (
          <div className="flex items-center gap-2 flex-wrap justify-center flex-1">
            {chip('Мін. позитивний', sm.minPos, POS_C)}
            {chip('Макс. позитивний', sm.maxPos, POS_C)}
            {chip('Мін. негативний', sm.minNeg, NEG_C)}
            {chip('Макс. негативний', sm.maxNeg, NEG_C)}
          </div>
        )}
        <div className="shrink-0"><DateNav value={date} onChange={setDate} min={min} max={max} /></div>
      </div>

      <div className="mb-3"><ViewToggle view={view} setView={setView} /></div>

      {loading ? (
        <div className="flex items-center justify-center text-xs" style={{ color: T.textMuted, height: 420 }}>Завантаження…</div>
      ) : hours.length === 0 ? (
        <div className="flex items-center justify-center text-xs text-center" style={{ color: T.textMuted, height: 420 }}>
          Немає цін небалансів за {dmy(date)}.
        </div>
      ) : view === 'chart' ? (
        <div style={{ height: 440 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={hours} margin={{ top: 10, right: 12, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.cardBorder} vertical={false} />
              <XAxis dataKey="hh" tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} interval={0} />
              <YAxis width={72} tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => v.toLocaleString('uk-UA')}
                label={{ value: 'Ціна Балансуючого ринку, грн/МВт·год', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
              <Tooltip content={<BrTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="pos" name="Позитивний небаланс" stroke={POS_C} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
              <Line type="monotone" dataKey="neg" name="Негативний небаланс" stroke={NEG_C} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <TransposedTable
          hours={hours.map((h) => h.hh)}
          rows={[
            { label: 'Позитивний небаланс, грн/МВт·год', cells: hours.map((h) => num2(h.pos)), color: POS_C },
            { label: 'Негативний небаланс, грн/МВт·год', cells: hours.map((h) => num2(h.neg)), color: NEG_C },
          ]} />
      )}
    </div>
  );
}

// ---- 6 РДН price tiles (presentational — date is owned by the page) ----
function RdnSummaryTiles({ summary, loading }: { summary: DamSummary | null; loading: boolean }) {
  const T = useTokens();
  const t = summary?.tiles || null;
  const val = (x: number | null | undefined) => (x == null ? 'N/A' : num(x, 2));

  const ChangeRow = ({ pct }: { pct: number | null | undefined }) =>
    pct != null ? (
      <div className="text-xs font-semibold mt-0.5" style={{ color: pct > 0 ? UP_C : pct < 0 ? DOWN_C : T.textMuted }}>
        {pct > 0 ? '↑' : pct < 0 ? '↓' : '→'} {num(Math.abs(pct), 2)}%
      </div>
    ) : <div className="text-xs mt-0.5" style={{ color: T.textDim }}>—</div>;

  const Tile = ({ title, z, sub }: { title: string; z?: Zone; sub: string }) => (
    <div className="rounded-xl p-3 flex flex-col" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, minHeight: 96 }}>
      <div className="text-[11px] font-medium" style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{title}</div>
      <div className="text-xl font-bold mt-1 leading-tight" style={{ color: z?.wavg == null ? T.textDim : T.textMain }}>{val(z?.wavg)}</div>
      <ChangeRow pct={z?.changePct} />
      <div className="text-[10px] mt-auto pt-1" style={{ color: T.textDim }}>{sub}</div>
    </div>
  );

  const D1 = 'грн/МВт·год · vs D−1';
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
      style={{ opacity: loading ? 0.5 : 1, transition: 'opacity .15s' }}>
      <Tile title="За добу" z={t?.day} sub={D1} />
      <Tile title="День (8-23)" z={t?.day_8_23} sub={D1} />
      <Tile title="Ніч (1-7, 24)" z={t?.night} sub={D1} />
      <Tile title="Пік (18-23)" z={t?.peak} sub={D1} />
      <Tile title="Напівпік (8-17)" z={t?.semipeak} sub={D1} />
      <Tile title="За місяць"
        z={t ? { wavg: t.month.wavg, prevWavg: t.month.prevWavg, changePct: t.month.changePct } : undefined}
        sub={`грн/МВт·год · ${t?.month.ym || ''} vs ${t?.month.prevYm || ''}`} />
    </div>
  );
}

export default function MarketPricesPage() {
  const T = useTokens();
  const [tab, setTab] = useState<Tab>('rdn');
  const [view, setView] = useState<View>('chart');
  // Single source of truth for the РДН tab: one date drives both tiles and the journal.
  const [date, setDate] = useState('');            // '' → backend resolves to latest priced day
  const [summary, setSummary] = useState<DamSummary | null>(null);
  const [dam, setDam] = useState<DamResp | null>(null);
  const [sumLoading, setSumLoading] = useState(true);
  const [damLoading, setDamLoading] = useState(true);

  useEffect(() => {
    if (tab !== 'rdn') return;
    setSumLoading(true);
    authFetch(`${API_BASE_URL}/market/dam-summary${date ? `?date=${date}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: DamSummary) => { setSummary(d); if (!date && d.tradeDate) setDate(d.tradeDate); })
      .catch(() => setSummary(null))
      .finally(() => setSumLoading(false));
  }, [tab, date]);

  useEffect(() => {
    if (tab !== 'rdn' || !date) return;
    setDamLoading(true);
    authFetch(`${API_BASE_URL}/market/dam-hourly?date=${date}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setDam(d))
      .catch(() => setDam(null))
      .finally(() => setDamLoading(false));
  }, [tab, date]);

  const hours: HourRow[] = useMemo(
    () => (dam?.hours || []).map((h) => ({ ...h, hh: String(h.hour).padStart(2, '0') })), [dam]);
  const volMax = useMemo(() => {
    const mx = Math.max(0, ...hours.map((h) => h.volume ?? 0));
    return mx > 0 ? Math.ceil(mx / 1000) * 1000 : 5000;
  }, [hours]);

  const TABS: { k: Tab; label: string }[] = [
    { k: 'rdn', label: 'РДН (на добу наперед)' },
    { k: 'br_oper', label: 'БР — оперативні' },
    { k: 'br_fact', label: 'БР — фактичні' },
  ];

  return (
    <DashboardLayout breadcrumb={['Ринок, ціни']}>
      <div style={{ color: T.textMain, maxWidth: '100%' }}>
        {/* market tabs */}
        <div className="flex items-center gap-1 flex-wrap mb-4">
          {TABS.map((tb) => {
            const active = tab === tb.k;
            return (
              <button key={tb.k} onClick={() => setTab(tb.k)} className="text-sm rounded-lg transition-colors"
                style={{
                  padding: '6px 12px',
                  background: active ? (T.isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB') : (T.isDark ? 'rgba(255,255,255,0.04)' : '#F3F4F6'),
                  color: active ? T.textMain : T.textMuted, fontWeight: active ? 600 : 400,
                  border: `1px solid ${active ? (T.isDark ? 'rgba(255,255,255,0.18)' : '#D1D5DB') : 'transparent'}`,
                }}>
                {tb.label}
              </button>
            );
          })}
        </div>

        {tab === 'rdn' ? (
          <>
            {/* shared date navigator + section heading */}
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-lg font-bold" style={{ color: T.textMain }}>Середньозважені ціни РДН</div>
                <div className="text-xs mt-0.5" style={{ color: T.textMuted }}>
                  джерело даних: АТ «Оператор ринку»{' '}
                  <a href={OREE_URL} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: VOL }}>
                    www.oree.com.ua
                  </a>
                </div>
              </div>
              <DateNav value={date} onChange={setDate}
                min={summary?.dataRange?.min} max={summary?.dataRange?.max} bold />
            </div>

            <div className="mb-4"><RdnSummaryTiles summary={summary} loading={sumLoading} /></div>

            <div className="rounded-xl p-4 sm:p-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
              <div className="text-lg font-bold mb-3" style={{ color: T.textMain }}>Погодинні ціни РДН</div>
              <div className="mb-3"><ViewToggle view={view} setView={setView} /></div>

              {damLoading ? (
                <div className="flex items-center justify-center text-xs" style={{ color: T.textMuted, height: 360 }}>Завантаження…</div>
              ) : hours.length === 0 ? (
                <div className="flex items-center justify-center text-xs text-center" style={{ color: T.textMuted, height: 360 }}>
                  Немає даних РДН за {dmy(date)}.<br />Ціни на завтра зʼявляються сьогодні о 12:45–13:15 за київським часом.
                </div>
              ) : view === 'chart' ? (
                <div style={{ height: 420 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={hours} margin={{ top: 10, right: 4, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.cardBorder} vertical={false} />
                      <XAxis dataKey="hh" tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis yAxisId="price" domain={[0, 16000]} ticks={PRICE_TICKS} width={64}
                        tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                        tickFormatter={(v: number) => v.toLocaleString('uk-UA')}
                        label={{ value: 'Ціна, грн/МВт·год', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
                      <YAxis yAxisId="vol" orientation="right" domain={[0, volMax]} width={56}
                        tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                        label={{ value: 'Обсяг, МВт·год', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
                      <Tooltip contentStyle={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 12, color: T.textMain }}
                        formatter={(v: any, n: any) => (n === 'Ціна' ? [`${num(v, 0)} грн/МВт·год`, n] : [`${num(v, 1)} МВт·год`, n])}
                        labelFormatter={(l: string) => `Година ${l}:00`} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="vol" dataKey="volume" name="Обсяг" fill={VOL} fillOpacity={0.18} stroke={VOL} strokeWidth={1} strokeOpacity={1} maxBarSize={26} />
                      <Line yAxisId="price" type="monotone" dataKey="price" name="Ціна" stroke={PRICE} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <TransposedTable
                  hours={hours.map((h) => h.hh)}
                  rows={[
                    { label: 'Ціна, грн/МВт·год', cells: hours.map((h) => num(h.price, 0)), color: PRICE },
                    { label: 'Обсяг, МВт·год', cells: hours.map((h) => num(h.volume, 1)), color: VOL },
                  ]} />
              )}
            </div>
          </>
        ) : tab === 'br_fact' ? (
          <BrPricesView key="br_fact" endpoint="/market/br-fact-hourly"
            title="Фактичні погодинні ціни Балансуючого ринку" />
        ) : (
          <BrPricesView key="br_oper" endpoint="/market/br-oper-hourly"
            title="Оперативні погодинні ціни Балансуючого ринку" />
        )}
      </div>
    </DashboardLayout>
  );
}
